import { useCallback, useEffect, useRef, useState } from "react";

const AXIS_LOCK_THRESHOLD = 10;
const ROTATION_DEGREES_PER_PX = 0.55;
const DRAWER_SNAP_RATIO = 0.28;
const DRAWER_VELOCITY_THRESHOLD = 0.25;
const FLIP_SNAP_THRESHOLD = 90;
const FLIP_VELOCITY_THRESHOLD = 0.35;
const FLIP_SETTLE_MS = 420;
const TAP_MOVE_THRESHOLD = 10;
const TAP_MAX_MS = 280;

type DragAxis = "rotate" | "drawer" | null;
type SettledFace = "front" | "back";

export function useDeckCardExpandGestures({
  open,
  drawerMaxHeight,
  isCompact = false,
  onCardTap,
}: {
  open: boolean;
  drawerMaxHeight: number;
  isCompact?: boolean;
  onCardTap?: () => void;
}) {
  const [rotationY, setRotationY] = useState(0);
  const [drawerOffset, setDrawerOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [isFlipSettling, setIsFlipSettling] = useState(false);
  const [settledFace, setSettledFace] = useState<SettledFace>("front");

  const dragAxisRef = useRef<DragAxis>(null);
  const isDraggingRef = useRef(false);
  const pointerStartRef = useRef({ x: 0, y: 0, time: 0 });
  const rotationStartRef = useRef(0);
  const drawerStartRef = useRef(0);
  const rotationRef = useRef(0);
  const drawerOffsetRef = useRef(0);
  const gestureLayerRef = useRef<HTMLDivElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const cardRotateRef = useRef<HTMLDivElement>(null);
  const cardTapRef = useRef<HTMLDivElement>(null);
  const pointerStartOnCardRef = useRef(false);
  const settledFaceRef = useRef<SettledFace>("front");
  const rotationRafRef = useRef<number | null>(null);
  const drawerRafRef = useRef<number | null>(null);
  const flipStartTimeRef = useRef<number>(0);
  const flipSettleTimeoutRef = useRef<number | null>(null);
  const controlRotationFrameRef = useRef<number | null>(null);
  const drawerMaxHeightRef = useRef(drawerMaxHeight);

  useEffect(() => {
    drawerMaxHeightRef.current = drawerMaxHeight;
  }, [drawerMaxHeight]);

  useEffect(() => {
    settledFaceRef.current = settledFace;
  }, [settledFace]);

  const getSettledRotation = useCallback(() => (settledFaceRef.current === "back" ? 180 : 0), []);

  const clearFlipSettleTimeout = useCallback(() => {
    if (flipSettleTimeoutRef.current == null) return;
    window.clearTimeout(flipSettleTimeoutRef.current);
    flipSettleTimeoutRef.current = null;
  }, []);

  const clearControlRotationFrame = useCallback(() => {
    if (controlRotationFrameRef.current == null) return;
    window.cancelAnimationFrame(controlRotationFrameRef.current);
    controlRotationFrameRef.current = null;
  }, []);

  const applyDrawerOffset = useCallback((offset: number) => {
    const maxHeight = drawerMaxHeightRef.current;
    const clamped = Math.max(0, Math.min(maxHeight, offset));
    drawerOffsetRef.current = clamped;
    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateY(${maxHeight - clamped}px)`;
    }
    setDrawerOffset(clamped);
  }, []);

  const applyRotation = useCallback(
    (value: number) => {
      const clamped = Math.max(0, Math.min(180, value));
      const nextSettledFace: SettledFace | null =
        clamped === 0 ? "front" : clamped === 180 ? "back" : null;
      clearFlipSettleTimeout();
      setIsFlipSettling(Boolean(nextSettledFace));
      rotationRef.current = clamped;
      if (cardRotateRef.current) {
        cardRotateRef.current.style.transform = `translateZ(0) rotateY(${clamped}deg)`;
      }
      setRotationY(clamped);
      if (nextSettledFace) {
        flipSettleTimeoutRef.current = window.setTimeout(() => {
          flipSettleTimeoutRef.current = null;
          setSettledFace(nextSettledFace);
          setIsFlipSettling(false);
          setRotationY(clamped);
          if (cardRotateRef.current) {
            cardRotateRef.current.style.transition = "none";
            cardRotateRef.current.style.removeProperty("transform");
          }
        }, FLIP_SETTLE_MS);
      }
    },
    [clearFlipSettleTimeout]
  );

  const animateRotation = useCallback(
    (value: number) => {
      const clamped = Math.max(0, Math.min(180, value));
      const current = cardRotateRef.current ? rotationRef.current : getSettledRotation();
      if (clamped === current) return;
      clearFlipSettleTimeout();
      clearControlRotationFrame();
      rotationRef.current = current;
      setRotationY(current);
      setIsFlipSettling(true);

      const runAnimatedRotation = () => {
        if (cardRotateRef.current) {
          cardRotateRef.current.style.transition = "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)";
        }
        applyRotation(clamped);
      };

      if (cardRotateRef.current) {
        runAnimatedRotation();
        return;
      }

      controlRotationFrameRef.current = window.requestAnimationFrame(() => {
        controlRotationFrameRef.current = window.requestAnimationFrame(() => {
          controlRotationFrameRef.current = null;
          runAnimatedRotation();
        });
      });
    },
    [applyRotation, clearControlRotationFrame, clearFlipSettleTimeout, getSettledRotation]
  );

  const scheduleDrawerOffset = useCallback((offset: number) => {
    const maxHeight = drawerMaxHeightRef.current;
    drawerOffsetRef.current = Math.max(0, Math.min(maxHeight, offset));
    if (drawerRafRef.current != null) return;
    drawerRafRef.current = window.requestAnimationFrame(() => {
      drawerRafRef.current = null;
      const next = drawerOffsetRef.current;
      if (drawerRef.current) {
        drawerRef.current.style.transform = `translateY(${maxHeight - next}px)`;
      }
      setDrawerOffset(next);
    });
  }, []);

  const scheduleRotation = useCallback((value: number) => {
    rotationRef.current = Math.max(0, Math.min(180, value));
    if (rotationRafRef.current != null) return;
    rotationRafRef.current = window.requestAnimationFrame(() => {
      rotationRafRef.current = null;
      if (cardRotateRef.current) {
        cardRotateRef.current.style.transform = `translateZ(0) rotateY(${rotationRef.current}deg)`;
      }
    });
  }, []);

  const resetInteractionState = useCallback(() => {
    if (rotationRafRef.current != null) {
      window.cancelAnimationFrame(rotationRafRef.current);
      rotationRafRef.current = null;
    }
    clearControlRotationFrame();
    rotationRef.current = 0;
    drawerOffsetRef.current = 0;
    clearFlipSettleTimeout();
    setRotationY(0);
    setDrawerOffset(0);
    setIsDragging(false);
    setIsFlipSettling(false);
    setSettledFace("front");
    isDraggingRef.current = false;
    dragAxisRef.current = null;
    if (cardRotateRef.current) {
      cardRotateRef.current.style.transform = "translateZ(0) rotateY(0deg)";
    }
    if (drawerRef.current) {
      drawerRef.current.style.transform = `translateY(${drawerMaxHeightRef.current}px)`;
    }
  }, [clearControlRotationFrame, clearFlipSettleTimeout]);

  useEffect(() => {
    if (!open) resetInteractionState();
    return () => {
      clearFlipSettleTimeout();
      clearControlRotationFrame();
    };
  }, [open, resetInteractionState, clearFlipSettleTimeout, clearControlRotationFrame]);

  const snapDrawer = useCallback((offset: number, velocityY = 0) => {
    const maxHeight = drawerMaxHeightRef.current;
    if (velocityY < -DRAWER_VELOCITY_THRESHOLD) return maxHeight;
    if (velocityY > DRAWER_VELOCITY_THRESHOLD) return 0;
    return offset >= maxHeight * DRAWER_SNAP_RATIO ? maxHeight : 0;
  }, []);

  const toggleDrawer = useCallback(
    (openDrawer: boolean) => {
      applyDrawerOffset(openDrawer ? drawerMaxHeightRef.current : 0);
    },
    [applyDrawerOffset]
  );

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, a, [data-no-card-tap]")) {
      return;
    }
    gestureLayerRef.current?.setPointerCapture(event.pointerId);
    const target = event.target as HTMLElement;
    pointerStartOnCardRef.current = Boolean(cardTapRef.current?.contains(target));
    pointerStartRef.current = { x: event.clientX, y: event.clientY, time: Date.now() };
    const settledRotation = getSettledRotation();
    rotationStartRef.current = settledRotation;
    rotationRef.current = settledRotation;
    setRotationY(settledRotation);
    drawerStartRef.current = drawerOffsetRef.current;
    flipStartTimeRef.current = Date.now();
    dragAxisRef.current = null;
    isDraggingRef.current = true;
    setIsDragging(true);
    clearFlipSettleTimeout();
    clearControlRotationFrame();
    setIsFlipSettling(false);
    if (cardRotateRef.current) {
      cardRotateRef.current.style.transition = "none";
      cardRotateRef.current.style.transform = `translateZ(0) rotateY(${rotationRef.current}deg)`;
    }
    if (drawerRef.current) drawerRef.current.style.transition = "none";
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    const nativeEvent = event.nativeEvent as PointerEvent;
    const latestPoint = nativeEvent.getCoalescedEvents?.().at(-1) ?? nativeEvent;
    const deltaX = latestPoint.clientX - pointerStartRef.current.x;
    const deltaY = latestPoint.clientY - pointerStartRef.current.y;
    if (!dragAxisRef.current) {
      if (Math.abs(deltaX) < AXIS_LOCK_THRESHOLD && Math.abs(deltaY) < AXIS_LOCK_THRESHOLD) {
        return;
      }
      const settled = settledFaceRef.current;
      const horizontalDominant = Math.abs(deltaX) >= Math.abs(deltaY) * (isCompact ? 0.85 : 1);
      const flipBackIntent = settled === "back" && deltaX < -AXIS_LOCK_THRESHOLD;
      const flipForwardIntent = settled === "front" && deltaX > AXIS_LOCK_THRESHOLD;
      if (horizontalDominant || flipBackIntent || flipForwardIntent) {
        dragAxisRef.current = "rotate";
      } else {
        // Swipe-up TOC drawer — re-enable later
        dragAxisRef.current = "drawer";
        return;
      }
    }
    if (dragAxisRef.current === "rotate") {
      scheduleRotation(rotationStartRef.current + deltaX * ROTATION_DEGREES_PER_PX);
      return;
    }
    // Swipe-up TOC drawer — re-enable later
    scheduleDrawerOffset(drawerStartRef.current - deltaY);
  };

  const finishPointer = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current) return;
    if (gestureLayerRef.current?.hasPointerCapture(event.pointerId)) {
      gestureLayerRef.current.releasePointerCapture(event.pointerId);
    }
    const deltaX = event.clientX - pointerStartRef.current.x;
    const deltaY = event.clientY - pointerStartRef.current.y;
    const elapsed = Date.now() - pointerStartRef.current.time;
    const moved = Math.hypot(deltaX, deltaY);
    const velocityY = deltaY / Math.max(elapsed, 16);

    if (
      pointerStartOnCardRef.current &&
      settledFaceRef.current !== "back" &&
      moved < TAP_MOVE_THRESHOLD &&
      elapsed < TAP_MAX_MS &&
      !dragAxisRef.current
    ) {
      onCardTap?.();
      pointerStartOnCardRef.current = false;
      isDraggingRef.current = false;
      setIsDragging(false);
      dragAxisRef.current = null;
      return;
    }

    if (drawerRef.current) {
      drawerRef.current.style.transition = "transform 0.45s cubic-bezier(0.22, 1, 0.36, 1)";
    }
    // Swipe-up TOC drawer — re-enable later
    if (dragAxisRef.current === "drawer") {
      applyDrawerOffset(snapDrawer(drawerOffsetRef.current, velocityY));
    }
    if (dragAxisRef.current === "rotate") {
      const current = rotationRef.current;
      const flipElapsed = Math.max(Date.now() - flipStartTimeRef.current, 16);
      const velocityDeg = (current - rotationStartRef.current) / flipElapsed;
      let target = 0;
      if (velocityDeg > FLIP_VELOCITY_THRESHOLD) target = 180;
      else if (velocityDeg < -FLIP_VELOCITY_THRESHOLD) target = 0;
      else target = current >= FLIP_SNAP_THRESHOLD ? 180 : 0;
      if (cardRotateRef.current) {
        cardRotateRef.current.style.transition = "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)";
      }
      applyRotation(target);
    }
    isDraggingRef.current = false;
    setIsDragging(false);
    dragAxisRef.current = null;
  };

  const renderFlatFace = !isDragging && !isFlipSettling && (rotationY === 0 || rotationY === 180);

  return {
    rotationY,
    drawerOffset,
    isDragging,
    settledFace,
    gestureLayerRef,
    drawerRef,
    cardRotateRef,
    cardTapRef,
    renderFlatFace,
    animateRotation,
    toggleDrawer,
    handlePointerDown,
    handlePointerMove,
    finishPointer,
  };
}
